import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

/** A short list inside a card: no bullets, one hairline between rows (docs/11 §4). */
export function List({ className, children }: { className?: string; children: ReactNode }) {
    return <ul className={cn('flex flex-col', className)}>{children}</ul>;
}

/**
 * One row of a short list — a notification, a parcel, a team member, a
 * connection (docs/11 §4): what it is on the first line, its quieter facts
 * under it, its state beside it and its own actions at the end, which wrap
 * under it on a narrow screen. An unread row carries a dot, not only a heavier
 * title, so it does not rely on weight alone.
 */
export function ListRow({
    leading,
    title,
    children,
    aside,
    actions,
    unread,
    as: Tag = 'li',
    className,
    onClick,
}: {
    /** What anchors the row on its left: a channel's tile, a thumbnail. */
    leading?: ReactNode;
    title: ReactNode;
    /** The quieter lines under the title. */
    children?: ReactNode;
    /** The row's state, between its text and its actions: badges. */
    aside?: ReactNode;
    actions?: ReactNode;
    unread?: boolean;
    /** A row outside a List is a div. */
    as?: 'li' | 'div';
    className?: string;
    /** Opens what the row is about — a claim's order, a connection's detail.
     * The row becomes a keyboard-reachable button; a click landing on `actions`
     * does not also open the row, since that would fire both at once. */
    onClick?: () => void;
}) {
    const stopForActions = (event: MouseEvent) => event.stopPropagation();
    return (
        <Tag
            data-slot="list-row"
            className={cn(
                'flex items-start gap-4 border-b border-border py-3 last:border-b-0 max-md:flex-wrap',
                onClick && 'cursor-pointer hover:bg-muted/50',
                className,
            )}
            {...(onClick && {
                role: 'button',
                tabIndex: 0,
                onClick,
                onKeyDown: (event: KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onClick();
                    }
                },
            })}
        >
            {leading}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className={cn('flex flex-wrap items-center gap-2', unread && 'font-medium')}>
                    {unread && <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-primary" />}
                    {title}
                </div>
                {children && <div className="flex flex-col gap-1 text-13 text-muted-foreground">{children}</div>}
            </div>
            {aside && <div className="flex flex-none flex-wrap items-center gap-2">{aside}</div>}
            {actions && (
                <div className="flex flex-none items-center gap-2" onClick={onClick ? stopForActions : undefined}>
                    {actions}
                </div>
            )}
        </Tag>
    );
}
